'use client';

import React, { useState, useCallback, useEffect } from 'react';
import type { Product, OrderItem } from '@/lib/supabaseService';
import { createClient } from '@/lib/supabase/client';
import HeroSection from './HeroSection';
import ProductGrid from './ProductGrid';
import OrderSummarySection from './OrderSummarySection';
import PreOrderForm from './PreOrderForm';
import ConfirmationScreen from './ConfirmationScreen';
import FooterSection from './FooterSection';

type CartItem = {
  productId: string;
  productName: string;
  sizeId: string;
  sizeLabel: string;
  quantity: number;
  unitPrice: number;
};

type ConfirmedOrder = {
  referenceNumber: string;
  villaNumber: string;
};

interface HomepageClientProps {
  initialProducts: Product[];
}

export default function HomepageClient({ initialProducts }: HomepageClientProps) {
  const [products, setProducts] = useState<Product[]>(initialProducts);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [confirmedOrder, setConfirmedOrder] = useState<ConfirmedOrder | null>(null);

  // Real-time product stock sync
  useEffect(() => {
    const supabase = createClient();
    const channel = supabase
      .channel('homepage_products')
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'products' },
        (payload) => {
          const updated = payload.new as {
            id: string;
            name: string;
            description: string;
            story: string;
            image: string;
            image_alt: string;
            sizes: Product['sizes'];
            active: boolean;
          };
          setProducts((prev) =>
            prev.map((p) =>
              p.id === updated.id
                ? {
                    ...p,
                    name: updated.name,
                    description: updated.description,
                    story: updated.story,
                    image: updated.image,
                    imageAlt: updated.image_alt,
                    sizes: updated.sizes,
                    active: updated.active,
                  }
                : p
            ).filter((p) => p.active)
          );
          // Also cap cart quantities if stock was reduced
          setCart((prev) =>
            prev.map((c) => {
              if (c.productId !== updated.id) return c;
              const size = updated.sizes.find((s) => s.id === c.sizeId);
              if (!size) return c;
              // Never cap or remove unlimited-inventory sizes
              if (size.unlimitedInventory) return c;
              const maxQty = size.stock;
              return maxQty <= 0 ? null : { ...c, quantity: Math.min(c.quantity, maxQty) };
            }).filter(Boolean) as CartItem[]
          );
        }
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'products' },
        (payload) => {
          const inserted = payload.new as {
            id: string;
            name: string;
            description: string;
            story: string;
            image: string;
            image_alt: string;
            sizes: Product['sizes'];
            active: boolean;
          };
          if (inserted.active) {
            setProducts((prev) => {
              const exists = prev.some((p) => p.id === inserted.id);
              if (exists) return prev;
              return [
                ...prev,
                {
                  id: inserted.id,
                  name: inserted.name,
                  description: inserted.description,
                  story: inserted.story,
                  image: inserted.image,
                  imageAlt: inserted.image_alt,
                  sizes: inserted.sizes,
                  active: inserted.active,
                },
              ];
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const handleSizeSelect = useCallback(
    (productId: string, productName: string, sizeId: string, sizeLabel: string, unitPrice: number) => {
      setCart((prev) => {
        const filtered = prev.filter((c) => c.productId !== productId);
        return [...filtered, { productId, productName, sizeId, sizeLabel, quantity: 1, unitPrice }];
      });
    },
    []
  );

  const handleQuantityChange = useCallback(
    (productId: string, delta: number) => {
      setCart((prev) =>
        prev
          .map((c) => {
            if (c.productId !== productId) return c;
            const product = products.find((p) => p.id === productId);
            const size = product?.sizes.find((s) => s.id === c.sizeId);
            // Unlimited inventory sizes have no stock-based upper limit
            const maxStock = size?.unlimitedInventory ? Infinity : (size?.stock ?? 1);
            const newQty = Math.min(Math.max(1, c.quantity + delta), maxStock);
            return { ...c, quantity: newQty };
          })
          .filter((c) => c.quantity > 0)
      );
    },
    [products]
  );

  const handleRemoveFromCart = useCallback((productId: string) => {
    setCart((prev) => prev.filter((c) => c.productId !== productId));
  }, []);

  const handleOrderConfirmed = useCallback(
    (refNumber: string, villaNumber: string) => {
      setConfirmedOrder({ referenceNumber: refNumber, villaNumber });
      setCart([]);
    },
    []
  );

  const orderItems: OrderItem[] = cart.map((c) => ({
    productId: c.productId,
    productName: c.productName,
    sizeId: c.sizeId,
    sizeLabel: c.sizeLabel,
    quantity: c.quantity,
    unitPrice: c.unitPrice,
    lineTotal: c.unitPrice * c.quantity,
  }));

  if (confirmedOrder) {
    return (
      <ConfirmationScreen
        referenceNumber={confirmedOrder.referenceNumber}
        villaNumber={confirmedOrder.villaNumber}
        onOrderAgain={() => setConfirmedOrder(null)}
      />
    );
  }

  return (
    <main className="min-h-screen bg-background">
      <HeroSection />
      <ProductGrid
        products={products}
        cart={cart}
        onSizeSelect={handleSizeSelect}
        onQuantityChange={handleQuantityChange}
      />
      {cart.length > 0 && (
        <OrderSummarySection items={orderItems} onRemove={handleRemoveFromCart} />
      )}
      <PreOrderForm
        items={orderItems}
        products={products}
        onOrderConfirmed={handleOrderConfirmed}
      />
      <FooterSection />
    </main>
  );
}